import React, { useEffect, useState } from "react";
import { useKeycloak } from "@react-keycloak/web";
import logUserLogin from "utils/logUserLogin";
import getCurrentUser from "utils/GetCurrentUser";
import { setCurrentUserState } from "utils/setCurrentUserState";
import { isPartnerPortalRole, resolveEffectiveRole } from "helpers/role";
import { useNavigate } from "react-router-dom";
import { INVITED_VALUE } from "utils/constants";
import { AuthBranding as Branding } from "components/common/branding";

export const keycloakClientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID;
export const autoLoginRedirect = process.env.REACT_APP_AUTO_LOGIN_REDIRECT;

const AuthGuard: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { initialized, keycloak } = useKeycloak();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      if (initialized && keycloak.authenticated) {
        if (!isValidRole(keycloak)) {
          console.error("User does not have a valid role");
          setLoading(false);
          keycloak.logout();
          throw new Error("User is not a partner portal user");
        }
        const userId = keycloak.tokenParsed?.sub;
        const idToken = keycloak.token;
        if (userId && idToken) {
          const invited = isInvited(keycloak);
          const currentUserDetails = await getCurrentUser(idToken);

          if (invited) {
            navigate("/signup");
          } else {
            await logUserLogin(userId, idToken);
            await setCurrentUserState(
              initialized,
              keycloak,
              currentUserDetails
            );
          }
        }
      }
      setLoading(false);
    };

    if (initialized) {
      fetchUser();
    }
  }, [initialized, keycloak.authenticated]);

  if (!initialized || loading) {
    return <Branding />;
  }

  if (!keycloak.authenticated) {
    if (autoLoginRedirect) {
      keycloak.login();
    }
    return <div>Authenticating</div>;
  }

  return <>{children}</>;
};

function isValidRole(keycloak: Keycloak.KeycloakInstance): boolean {
  const roles =
    // @ts-ignore
    keycloak.tokenParsed?.resource_access?.[keycloakClientId]?.roles ?? [];
  const role = resolveEffectiveRole(roles);
  if (!role) {
    return false;
  }

  return isPartnerPortalRole(role);
}

export function isInvited(keycloak: Keycloak.KeycloakInstance): boolean {
  const roles =
    // @ts-ignore
    keycloak.tokenParsed?.resource_access?.[keycloakClientId]?.roles ?? [];
  return resolveEffectiveRole(roles) === INVITED_VALUE;
}

export default AuthGuard;
